/**
 * Sprint C — Avis consultatif sur l'attribution (AMO, équipe MOE, client).
 *
 * Affiché dans le rapport d'analyse des offres pour les rôles consultatifs
 * (moe_principal, moe_membre, client, amo). Permet de désigner une entreprise
 * préférée par lot, avec commentaire. L'avis n'attribue pas — seul l'architecte
 * attribue. Distinct de `NotationDCE` (multicritère).
 * RP66 (09-VOCABULAIRE) : « vote » → « avis sur l'attribution » à l'écran ; l'API
 * garde ses clés (`/vote`, `votes`).
 */
import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Megaphone, Trash } from '@phosphor-icons/react';
import { libelleRole } from '../../lib/libelles';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';

const API_URL = process.env.REACT_APP_BACKEND_URL;
// RP41 / D7 — l'AMO donne son avis sur l'attribution (après la date limite, D28).
const VOTERS_ROLES = ['moe_principal', 'moe_membre', 'client', 'amo'];

export default function VoteAttributionPanel({
  chantierId, dceId, lotId, entreprises, currentUser, votesLot, userRoleProjet, onVoted,
}) {
  const canVote = VOTERS_ROLES.includes(userRoleProjet);

  const myUserId = String(currentUser?._id || currentUser?.id || '');
  const myVote = votesLot && myUserId ? votesLot[myUserId] : null;

  const [selectedEnt, setSelectedEnt] = useState(myVote?.entreprise_id || '');
  const [comment, setComment] = useState(myVote?.comment || '');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setSelectedEnt(myVote?.entreprise_id || '');
    setComment(myVote?.comment || '');
  }, [myVote?.entreprise_id, myVote?.comment]);

  if (!canVote) return null;

  const submit = async () => {
    if (!selectedEnt || submitting) return;
    setSubmitting(true);
    try {
      await axios.post(
        `${API_URL}/api/chantiers/${chantierId}/dce/${dceId}/vote`,
        {
          lot_id: lotId,
          entreprise_id: selectedEnt,
          comment: comment.trim() || null,
        },
        { withCredentials: true },
      );
      toast.success('Avis enregistré');
      onVoted?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de l'enregistrement de l'avis");
    } finally {
      setSubmitting(false);
    }
  };

  const removeVote = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await axios.delete(
        `${API_URL}/api/chantiers/${chantierId}/dce/${dceId}/vote/${lotId}`,
        { withCredentials: true },
      );
      toast.success('Avis retiré');
      setSelectedEnt('');
      setComment('');
      onVoted?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="border-l-4 border-[#7B5EA7] bg-[#F5F0FB] px-4 py-3 mt-3"
      data-testid={`vote-attribution-panel-${lotId}`}
    >
      <div className="flex items-center gap-2 mb-1">
        <Megaphone size={14} weight="fill" className="text-[#7B5EA7]" />
        <span className="font-bold text-sm text-[#4B3A6B]">Mon avis sur l'attribution de ce lot</span>
      </div>
      <p className="text-[11px] text-[#6B6B6B] mb-2">
        Avis consultatif — seul l'architecte attribue le lot.
      </p>

      <select
        value={selectedEnt}
        onChange={(e) => setSelectedEnt(e.target.value)}
        className="w-full px-2 py-1.5 border border-[#D6C8E8] rounded-none text-sm bg-white focus:outline-none focus:border-[#7B5EA7]"
        data-testid={`vote-select-${lotId}`}
      >
        <option value="">— Aucune préférence —</option>
        {(entreprises || []).map((e) => (
          <option key={e.id} value={e.id}>
            {e.nom}
          </option>
        ))}
      </select>

      <Textarea
        rows={2}
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        placeholder="Justification (optionnel)"
        className="rounded-none mt-2 text-sm focus:border-[#7B5EA7]"
        data-testid={`vote-comment-${lotId}`}
        maxLength={1000}
      />

      <div className="flex gap-2 mt-2">
        <Button
          size="sm"
          className="rounded-none bg-[#7B5EA7] hover:bg-[#5e4585] text-white"
          onClick={submit}
          disabled={!selectedEnt || submitting}
          data-testid={`vote-submit-${lotId}`}
        >
          {myVote ? 'Mettre à jour mon avis' : 'Enregistrer mon avis'}
        </Button>
        {myVote && (
          <Button
            size="sm"
            variant="outline"
            className="rounded-none border-[#7B5EA7] text-[#7B5EA7]"
            onClick={removeVote}
            disabled={submitting}
            data-testid={`vote-remove-${lotId}`}
          >
            <Trash size={12} className="mr-1" /> Retirer
          </Button>
        )}
      </div>
    </div>
  );
}


/**
 * Récap des avis d'un lot (visible archi/MOE) — par entreprise préférée.
 */
export function VoteRecapForLot({ votesLot, retenueId, noms = {} }) {
  const votesArr = Object.values(votesLot || {});
  if (votesArr.length === 0) return null;

  // Group by entreprise_id
  const byEnt = {};
  votesArr.forEach((v) => {
    const eid = v.entreprise_id;
    if (!eid) return;
    if (!byEnt[eid]) byEnt[eid] = [];
    byEnt[eid].push(v);
  });

  return (
    <div className="border-t border-[#E0E0DB] px-4 py-3 bg-[#F5F0FB]" data-testid="vote-recap">
      <p className="text-xs font-bold uppercase tracking-wider text-[#4B3A6B] mb-2 flex items-center gap-1">
        <Megaphone size={12} weight="fill" /> Avis sur l'attribution ({votesArr.length})
      </p>
      <div className="space-y-1.5">
        {Object.entries(byEnt).map(([entId, vs]) => (
          <div
            key={entId}
            className={`text-xs px-2 py-1 border-l-2 ${entId === retenueId ? 'border-[#2D6A4F] bg-[#E8F3EE]' : 'border-[#7B5EA7] bg-white'}`}
            data-testid={`vote-recap-ent-${entId}`}
          >
            <div className="font-semibold text-[#2C2C2C]">
              {vs.length} avis en faveur de{' '}
              {/* A6 (P2#5) — le nom de l'entreprise, jamais son identifiant */}
              <span className="text-[#7B5EA7]">{vs[0].entreprise_nom || noms[entId] || 'une entreprise'}</span>
            </div>
            {vs.map((v, i) => (
              <div key={i} className="text-[#6B6B6B] mt-0.5">
                ✓ {v.user_name || 'Anonyme'}{' '}
                <span className="text-[10px] text-[#7B5EA7]">({libelleRole(v.user_role_projet)})</span>
                {v.comment && (
                  <span className="block italic ml-3 text-[#4B3A6B]">"{v.comment}"</span>
                )}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
